/**
 * Stable identity for schedule assignments.
 *
 * A saved assignment has a DB `id`. A freshly created one (click or drag) has
 * none until the next save round-trip, so the editor used to match blocks by
 * comparing every field including the times. That is fragile: the moment a
 * producer resizes or nudges a brand-new block its times change, and any code
 * that tried to re-find it by its *original* times (or that compared "20:05"
 * against a DB "20:05:00") could miss it — so the resize silently reverted and
 * the block "couldn't be resized".
 *
 * The fix is a transient client id, minted at creation and never persisted
 * (the save serialiser whitelists DB columns, so `__clientId` is dropped just
 * like the `__mirror` preview flags). Matching then prefers `id`, then
 * `__clientId`, and only falls back to the field tuple for legacy rows.
 */

export interface AssignmentIdentity {
  id?: string;
  __clientId?: string;
  crew_member_id: string;
  assignment_type: "artist" | "custom";
  stage_id?: string | null;
  stage_name: string;
  lineup_id?: string | null;
  artist_name?: string;
  subject?: string;
  filming_start: string;
  filming_end: string;
}

let counter = 0;

/** Mint a unique, session-scoped client id. Prefixed so it can never be
 *  mistaken for (or collide with) a UUID DB id. */
export function createClientId(): string {
  const c =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${counter++}-${performance.now()}`;
  return `client-${c}`;
}

/** Attach a client id when the assignment doesn't already have a stable id. */
export function withClientId<T extends AssignmentIdentity>(assignment: T): T {
  if (assignment.id || assignment.__clientId) return assignment;
  return { ...assignment, __clientId: createClientId() };
}

/**
 * True when two assignments are the same block. Prefers stable ids; only
 * legacy rows with neither fall back to the field+time tuple (FK-aware, matching
 * the previous inline behaviour so nothing regresses).
 */
export function matchAssignments(a: AssignmentIdentity, b: AssignmentIdentity): boolean {
  if (a.id && b.id) return a.id === b.id;
  if (a.__clientId && b.__clientId) return a.__clientId === b.__clientId;
  // Mixed id kinds (one side already saved with a DB id, the other still a
  // pre-save __clientId copy) fall through to the field tuple. That lets a stale
  // reference held across a save round-trip still match its now-saved row; a
  // coincidental match between two truly different blocks can't happen because
  // overlap checks forbid two blocks sharing crew + time.

  const stageMatches =
    a.stage_id && b.stage_id ? a.stage_id === b.stage_id : a.stage_name === b.stage_name;

  const artistMatches =
    a.lineup_id && b.lineup_id
      ? a.lineup_id === b.lineup_id
      : a.artist_name === b.artist_name || (!a.artist_name && !b.artist_name);

  return (
    a.crew_member_id === b.crew_member_id &&
    a.assignment_type === b.assignment_type &&
    stageMatches &&
    artistMatches &&
    (a.subject === b.subject || (!a.subject && !b.subject)) &&
    a.filming_start === b.filming_start &&
    a.filming_end === b.filming_end
  );
}

/**
 * Index-independent identity string for a block. Used to mark a block as
 * "animating out" from a handler (which has no render index) and to read that
 * mark back during render. Prefers stable ids; the field composite is a
 * best-effort fallback for legacy rows.
 */
export function assignmentStableId(a: AssignmentIdentity): string {
  return (
    a.id ??
    a.__clientId ??
    `${a.crew_member_id}|${a.stage_name}|${a.artist_name ?? a.subject ?? ""}|${a.filming_start}|${a.filming_end}`
  );
}
